import { useAuth } from "hooks/useAuth";
import { http } from "middleware/apiMiddleware";
import { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ReactComponent as Logo } from "assets/RewardsLogo.svg";
import { signIn } from "constants/apiUrls";
import { useLazyUserQuery } from "store/apis/userApi";
import { toast } from "react-hot-toast";
import useMixpanel from "hooks/useMixpanel";
import { routes } from "constants/routes";
import { useNavigate } from "react-router-dom";
import Button from "components/Buttons/Button";
import InputField from "components/FormFields/InputField";
import { PENDING_INVITE_TOKEN_KEY, SKIP_ONBOARDING_KEY, extractEmailFromJwt as extractEmailFromInviteToken } from "containers/InviteHandler";

const validationSchema = Yup.object({
  username: Yup.string()
    .email("Please enter a valid email address")
    .required("Email address is required"),
  password: Yup.string().required("Password is required"),
});

export const Login = () => {
  const { trackEvent } = useMixpanel();
  const [isloggingIn, setIslogginingIn] = useState(false);
  const [fetchUser, { isFetching }] = useLazyUserQuery();
  const authContext = useAuth();
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      username: "",
      password: "",
    },
    validationSchema,
    validateOnMount: false,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      const payload = {
        email: values.username,
        password: values.password,
      };

      try {
        trackEvent("sign_in_button_Clicking", { username: values.username });
        setIslogginingIn(true);
        const { data, error } = await http.post(signIn, payload, false);
        if (!!data && !error) {
          const userResponse = await fetchUser({
            token: data?.access,
            uuid: data?.uuid,
          });
          if (userResponse?.isSuccess) {
            trackEvent("sign_in_success", { signInResponse: data });
            const pendingInviteToken = sessionStorage.getItem(PENDING_INVITE_TOKEN_KEY);
            const navigationUrl = pendingInviteToken
              ? `/invite/${pendingInviteToken}`
              : `${routes.chooseBusiness}/1`;
            await authContext?.login(
              { data: { ...userResponse.data, ...data } },
              navigationUrl,
            );
          }
        } else {
          trackEvent("sign_in_failure", { error, username: values.username });
          toast.error("Invalid username or password");
        }
      } catch (error) {
        trackEvent("sign_in_failure_unexpected", { error, username: values.username });
        toast.error("unexpected error occurred while login");
        setIslogginingIn(false);
      } finally {
        setIslogginingIn(false);
      }
    },
  });

  const handleSignUp = () => {
    const pendingInviteToken = sessionStorage.getItem(PENDING_INVITE_TOKEN_KEY);
    if (pendingInviteToken) {
      sessionStorage.setItem(SKIP_ONBOARDING_KEY, 'true');
      const params = new URLSearchParams();
      params.set('invitationToken', pendingInviteToken);
      const email = extractEmailFromInviteToken(pendingInviteToken);
      if (email) params.set('email', email);
      navigate(`${routes.signUp}?${params.toString()}`);
    } else {
      navigate(routes.signUp);
    }
  };

  return (
    <div className="flex flex-col w-full gap-4 md:gap-6 py-4 md:py-0">
      {/* Welcome Section */}
      <div className="welcome-section space-y-2">
        <div className="text-lg mb-2 ml-2">Welcome to</div>
        <Logo width={180} height={50} />
        <p className="text-sm text-gray-500 pt-2">
          Enter your credentials to access account
        </p>
      </div>

      {/* Form */}
      <form
        onSubmit={formik.handleSubmit}
        id="login-form"
        className="form-fields space-y-4"
      >
        {/* Email Field */}
        <InputField
          label="Email Address"
          type="email"
          value={formik.values.username}
          placeholder="Enter email address"
          onChange={(e) => formik.setFieldValue("username", e.target.value)}
          error={formik.touched.username && formik.errors.username}
          customClass="py-2.5"
        />

        {/* Password Field */}
        <InputField
          label="Password"
          type="password"
          value={formik.values.password}
          placeholder="Enter password"
          onChange={(e) => formik.setFieldValue("password", e.target.value)}
          error={formik.touched.password && formik.errors.password}
          customClass="py-2.5"
          showPasswordToggle
        />

        {/* Forgot Password Link */}
        <div className="text-right">
          <button
            type="button"
            onClick={() => navigate(routes.forgotPassword)}
            className="text-sm text-[#5856D6] hover:underline"
          >
            Forgot password?
          </button>
        </div>

        {/* Buttons */}
        <div className="flex justify-between gap-3 pt-2 button-container">
          <Button
            type="button"
            variant="secondary"
            label="Sign up"
            onClick={handleSignUp}
            customClass="!w-[147px] !h-[40px] !py-4 !px-0 !rounded !border !border-[#0102CE] !bg-white !text-[#0102CE] !font-poppins !font-medium !text-sm !leading-none"
          />
          <Button
            type="submit"
            variant="primary"
            label="Sign in"
            disabled={!formik.isValid || isFetching || isloggingIn || !formik.dirty}
            loading={isFetching || isloggingIn}
            form="login-form"
            customClass="!w-[238px] !h-[40px] !py-4 !px-0 !rounded !bg-[#0102CE] !text-white !font-poppins !font-medium !text-sm !leading-none !text-center"
          />
        </div>
      </form>
    </div>
  );
};

export default Login;
